import { useContext, useEffect, useReducer, useState } from "react";
import Button from "react-bootstrap/Button";
import Container from "react-bootstrap/Container";
import Form from "react-bootstrap/Form";
import { Helmet } from "react-helmet-async";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "../SigninScreen.css";
import { Store } from "../Store";
import OtpValidationModal from "../components/OtpValidationModal";
import useAxios from "../hooks/useAxios";
import { getError } from "../utils";

const reducer = (state, action) => {
  switch (action.type) {
    case "CREATE_REQUEST":
      return { ...state, loadingCreate: true };
    case "CREATE_SUCCESS":
      return {
        ...state,
        loadingCreate: false,
      };
    case "CREATE_FAIL":
      return { ...state, loadingCreate: false };
    case "FETCH_REQUEST":
      return { ...state, loading: true };
    case "FETCH_SUCCESS":
      return { ...state, loading: false };
    case "FETCH_FAIL":
      return { ...state, loading: false, error: action.payload };
    case "UPDATE_REQUEST":
      return { ...state, loadingUpdate: true };
    case "UPDATE_SUCCESS":
      return { ...state, loadingUpdate: false };
    case "UPDATE_FAIL":
      return { ...state, loadingUpdate: false };
    case "UPLOAD_REQUEST":
      return { ...state, loadingUpload: true, errorUpload: "" };
    case "UPLOAD_SUCCESS":
      return {
        ...state,
        loadingUpload: false,
        errorUpload: "",
      };
    case "UPLOAD_FAIL":
      return { ...state, loadingUpload: false, errorUpload: action.payload };

    default:
      return state;
  }
};

export default function SigninNewPassword() {
  const Axios = useAxios();
  const navigate = useNavigate();
  const { search } = useLocation();
  const redirectInUrl = new URLSearchParams(search).get("redirect");
  const redirect = redirectInUrl ? redirectInUrl : "/";

  const [{ loading, error, loadingUpdate, loadingCreate }, dispatch] =
    useReducer(reducer, {
      loading: false,
      error: "",
      loadingCreate: false,
      loadingUpdate: false,
    });

  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [formValidated, setFormValidated] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showOtpValidation, setShowOtpValidation] = useState(false);
  const [otpValidated, setOtpValidated] = useState(false);
  const [otpValidatingStatus, setOtpValidatingStatus] = useState(false);
  const [customerOtp, setCustomerOtp] = useState("");

  const { state, dispatch: ctxDispatch } = useContext(Store);
  const { userInfo } = state;
  const [passwordStrength, setPasswordStrength] = useState("");

  const validatePassword = (password) => {
    if (password.length < 6) {
      setPasswordStrength("Şifre en az altı karakter olmalıdır");
    } else if (!/[A-Z]/.test(password)) {
      setPasswordStrength("Şifre en az bir büyük harf içermelidir");
    } else if (!/[a-z]/.test(password)) {
      setPasswordStrength("Şifre en az bir küçük harf içermelidir");
    } else if (!/[0-9]/.test(password)) {
      setPasswordStrength("Şifre en az bir rakam içermelidir");
    } else if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      setPasswordStrength("Şifre en az bir özel karakter içermelidir");
    } else {
      setPasswordStrength("Şifre güçlü");
    }
  };

  const handleValidateOtp = async (kod) => {
    try {
      const { data } = await Axios.post("/api/email/verify-otp", {
        to: email,
        otp: kod,
      });

      setShowOtpValidation(false);
      handleKayitFinish();
      setCustomerOtp("");
      setOtpValidatingStatus(false);
    } catch (err) {
      setOtpValidatingStatus(false);
      toast.error(getError(err));
    }
  };

  const handleKayitFinish = async () => {
    dispatch({ type: "CREATE_REQUEST" });
    try {
      await Axios.put("/api/busers/newpassword", {
        email,
        password,
      });
      navigate("/signin");
      dispatch({ type: "CREATE_SUCCESS" });
      toast.success("Şifreniz başarılı bir şekilde değiştirildi!");
    } catch (err) {
      toast.error(getError(err));
      dispatch({ type: "CREATE_FAIL" });
    }
  };

  useEffect(() => {
    if (userInfo) {
      navigate(redirect);
    }
  }, [navigate, redirect, userInfo]);

  const submitHandler = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (form.checkValidity() === false) {
      setFormValidated(true);
      return;
    }

    if (passwordStrength !== "Şifre güçlü") {
      toast.error("Lütfen daha güçlü bir şifre oluşturunuz!");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Şifre tekrarı hatalı");
      return;
    }

    dispatch({ type: "CREATE_REQUEST" });

    let name;
    try {
      const { data } = await Axios.get(`/api/busers/checkemail/${email}`);
      if (!data.emailInUse) {
        toast.error("Geçersiz e-posta adresi!");
        dispatch({ type: "CREATE_FAIL" });
        return;
      } else {
        name = data.name;
      }
    } catch (err) {
      toast.error("E-posta adresiniz kontrol edilirken hata alındı!");
      dispatch({ type: "CREATE_FAIL" });
      return;
    }

    if (!otpValidated) {
      try {
        await Axios.post("/api/email/send-otp/sifre-degistir", {
          to: email,
          name,
        });
        setShowOtpValidation(true);
        setCustomerOtp("");
      } catch (err) {
        toast.error(getError(err));
        dispatch({ type: "CREATE_FAIL" });
        return;
      }
      dispatch({ type: "CREATE_SUCCESS" });
      return;
    }
    dispatch({ type: "CREATE_SUCCESS" });
  };

  return (
    <Container className="signin-small-container">
      <Helmet>
        <title>Kolay Cafe Giriş</title>
      </Helmet>
      <div className="signin-form-header">
        <h1>Kolay Cafe</h1>
      </div>
      <Form
        onSubmit={submitHandler}
        noValidate
        validated={formValidated}
        className="signin-signin-form"
      >
        <Form.Group className="signin-form-group">
          <Form.Label>E-posta Adresiniz</Form.Label>
          <div className="input-icon-wrapper">
            <i className="fas fa-envelope"></i>
            <Form.Control
              className="signin-form-control with-icon"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              pattern="^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$"
            />
            <Form.Control.Feedback type="invalid">
              Lütfen geçerli bir e-posta adresi giriniz
            </Form.Control.Feedback>
          </div>
        </Form.Group>

        <Form.Group className="signin-form-group">
          <Form.Label>Şifre</Form.Label>
          <div className="input-icon-wrapper">
            <i className="fas fa-lock"></i>
            <Form.Control
              className="signin-form-control with-icon"
              type="password"
              required
              value={password}
              onChange={(e) => {
                const newPassword = e.target.value;
                setPassword(newPassword);
                validatePassword(newPassword);
              }}
            />
          </div>

          <small
            id="passwordHelpBlock"
            className={`signup-form-text ${
              passwordStrength === "Şifre güçlü"
                ? "text-success"
                : "text-danger"
            }`}
          >
            {passwordStrength}
          </small>
        </Form.Group>

        <Form.Group className="signin-form-group">
          <Form.Label>Şifre Tekrarı</Form.Label>
          <div className="input-icon-wrapper">
            <i className="fas fa-lock"></i>
            <Form.Control
              type="password"
              required
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="signin-form-control with-icon"
            />

            <Form.Control.Feedback type="invalid">
              Lütfen şifrenizi tekrar giriniz.
            </Form.Control.Feedback>
          </div>
        </Form.Group>

        <div className="signin-button-group">
          <Button
            type="submit"
            className="signin-submit-button"
            variant="light"
          >
            Şifreni Değiştir
          </Button>
        </div>
        <div className="signin-link-group">
          <Link
            to={`/signin?redirect=${redirect}`}
            className="signin-signup-link"
          >
            Giriş yapmak için tıkla!
          </Link>
        </div>
      </Form>

      <OtpValidationModal
        showOtpValidation={showOtpValidation}
        setShowOtpValidation={setShowOtpValidation}
        handleValidateOtp={handleValidateOtp}
        setCustomerOtp={setCustomerOtp}
        setOtpValidatingStatus={setOtpValidatingStatus}
        otpValidatingStatus={otpValidatingStatus}
        email={email}
      />
    </Container>
  );
}
